// Libraries
import 'dayjs/locale/pt'
import { useState, useEffect } from 'react'
// MUI
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
// Types
import { Services, KmsVehicle } from '../../../../../store/slices/timesheet/timesheetPageSlice'
// Hooks
import { useGetTimesheetQuery } from '../../../../../utils/hooks/useGetTimesheetQuery'
// Components
import ServiceSummary from './ServiceSummary'
import ServiceDetails from './ServiceDetails'

interface ServiceProps {
	serviceDetail: Services
	instance: number
}
function Service({ serviceDetail, instance }: Readonly<ServiceProps>): React.JSX.Element {
	const { isFetching } = useGetTimesheetQuery()
	const [kmsVehicles, setKmsVehicles] = useState<KmsVehicle[]>([
		{
			kmsIni: 0,
			kmsEnd: 0,
			inEmpty: false,
		},
	])

	/**
	 * This effect runs whenever the loading state of the timesheet changes.
	 * If the timesheet is not loading, it updates the `kmsVehicles` state with the values from `serviceDetail.serviceExecution.kmsVehicles`.
	 * If there are no `kmsVehicles` in the service execution, it defaults to an array with a single object containing default values for `kmsIni`, `kmsEnd`, and `inEmpty`.
	 * This ensures that the component has the necessary data to display the service details correctly.
	 *
	 * The dependency array includes `isFetching`, so the effect will re-run whenever this value changes, allowing the component to react to changes in the loading state of the timesheet data.
	 */
	useEffect(() => {
		if (isFetching) return
		setKmsVehicles(
			serviceDetail.serviceExecution?.kmsVehicles ?? [
				{
					kmsIni: 0,
					kmsEnd: 0,
					inEmpty: false,
				},
			],
		)
	}, [isFetching])

	return (
		<li
			style={{
				margin: 0,
				padding: 0,
				display: 'grid',
			}}
		>
			<Accordion disableGutters>
				<AccordionSummary
					sx={{
						padding: 0,
						backgroundColor: 'transparent',
						cursor: 'default !important',
						borderTop: '1px solid rgba(134, 149, 175, 0.15)',
						'& *.Mui-expanded': {
							backgroundColor: '#F5F7FC !important',
						},
						'& .MuiAccordionSummary-content': {
							margin: 0,
							padding: '12px 0',
						},
						'& .MuiAccordionSummary-expandIconWrapper': {
							height: 'inherit',
							margin: 0,
							padding: '12px 20px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							alignSelf: 'stretch',
						},
					}}
					expandIcon={<ExpandMoreIcon sx={{ color: 'var(--primary)' }} />}
				>
					<ServiceSummary
						serviceDetail={serviceDetail}
						instance={instance}
						kmsVehicles={kmsVehicles}
					/>
				</AccordionSummary>
				<AccordionDetails sx={{ margin: 0, padding: 0 }}>
					<ServiceDetails
						serviceDetail={serviceDetail}
						instance={instance}
					/>
				</AccordionDetails>
			</Accordion>
		</li>
	)
}

export default Service
